import {
  asFramingNailer,
  crewCoverage,
  FRAMING_NAILERS,
  framingNailerLabel,
  type Crew,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { deleteRow, updateRow } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { CrewDocumentsModal } from "./CrewDocumentsModal";
import { coverageText, NewCrewModal } from "./NewCrewModal";
import { SendCrewPacketModal } from "./SendCrewPacketModal";
import { ToolInput } from "./SuppliersTool";

interface CrewRow extends Crew {
  trades: string[];
  rateCount: number;
}

/** Crews, with trades collapsed to a count past two. */
export function CrewsTool() {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(60);
  const [error, setError] = useState<string | null>(null);
  /**
   * Whose papers are open, by id rather than by row.
   *
   * The modal writes to the crew and the list re-reads; holding the row would
   * mean holding the version from before the write, and the coverage banner
   * inside the modal would still say what it said this morning.
   */
  const [papersId, setPapersId] = useState<string | null>(null);
  /** The new-crew form, which writes nothing until it is submitted. */
  const [creating, setCreating] = useState(false);
  /** Whose subcontractor packet is being sent (ADR 0279). */
  const [packetId, setPacketId] = useState<string | null>(null);

  const query = useAsync(async () => {
    const [crews, crewTrades, trades, rates] = await Promise.all([
      supabase.from("crews").select("*").order("name"),
      supabase.from("crew_trades").select("crew_id,trade_id"),
      supabase.from("trades").select("id,name").order("sort_order"),
      supabase.from("crew_labor_rates").select("crew_id"),
    ]);

    const tradeName = new Map(
      unwrap<{ id: string; name: string }[]>(trades).map((t) => [t.id, t.name]),
    );
    const group = (
      rows: { crew_id: string; ref: string }[],
      names: Map<string, string>,
    ) => {
      const out = new Map<string, string[]>();
      for (const row of rows) {
        const name = names.get(row.ref);
        if (!name) continue;
        const list = out.get(row.crew_id);
        if (list) list.push(name);
        else out.set(row.crew_id, [name]);
      }
      return out;
    };

    const byTrade = group(
      unwrap<{ crew_id: string; trade_id: string }[]>(crewTrades).map((r) => ({
        crew_id: r.crew_id,
        ref: r.trade_id,
      })),
      tradeName,
    );
    const rateCounts = new Map<string, number>();
    for (const row of unwrap<{ crew_id: string }[]>(rates)) {
      rateCounts.set(row.crew_id, (rateCounts.get(row.crew_id) ?? 0) + 1);
    }

    return {
      crews: unwrap<Crew[]>(crews).map<CrewRow>((crew) => ({
        ...crew,
        trades: (byTrade.get(crew.id) ?? []).sort(),
        rateCount: rateCounts.get(crew.id) ?? 0,
      })),
      // The names as the tick boxes on the new-crew form need them.
      tradeList: [...tradeName].map(([id, name]) => ({ id, name })),
    };
  }, []);

  const all = query.data?.crews ?? [];
  const tradeList = query.data?.tradeList ?? [];
  const papers = all.find((crew) => crew.id === papersId) ?? null;
  const packetFor = all.find((crew) => crew.id === packetId) ?? null;
  const filtered = search
    ? all.filter((crew) =>
        `${crew.name} ${crew.contact_name ?? ""} ${crew.email ?? ""} ${crew.phone ?? ""} ${crew.trades.join(
          " ",
        )}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
    : all;
  const shown = filtered.slice(0, limit);

  const guard = async (action: () => Promise<void>) => {
    try {
      await action();
      setError(null);
      await query.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const collapse = (values: string[], count: (n: number) => string) =>
    values.length === 0
      ? "—"
      : values.length <= 2
        ? values.join(", ")
        : count(values.length);

  return (
    <>
      <ErrorNote error={error} />
      <div className="toolbar2">
        <input
          placeholder={t("Search…")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setLimit(60);
          }}
        />
        <span className="count">
          {tn(filtered.length, "1 crew", "{n} crews")}
          {filtered.length > shown.length
            ? t(" · showing {n}", { n: shown.length })
            : ""}
        </span>
        <button
          className="addrow"
          style={{ marginTop: 0 }}
          onClick={() => setCreating(true)}
        >
          {t("+ Create")}
        </button>
      </div>

      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              {/* The company, and who to call there. Everywhere else in the
                  app the two read as one: "Apex Metal (Brad Kiley)". */}
              <th>{t("Company")}</th>
              <th>{t("Contact")}</th>
              <th>{t("Phone")}</th>
              <th>{t("Email")}</th>
              <th>{t("Active")}</th>
              <th>{t("Can login")}</th>
              {/* Employees, not a subcontractor. Their work orders are costed
                  and tracked but never paid out through QuickBooks. */}
              <th>{t("In house")}</th>
              {/* The framing nailer they shoot. A material order's framing
                  nails are bought to fit it (ADR 0287). */}
              <th>{t("Nail gun")}</th>
              <th>{t("Trades")}</th>
              <th>{t("Insurance")}</th>
              <th>{t("Documents")}</th>
              <th className="num">{t("Labor costs")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={13} className="tnone">
                  {t("No crews.")}
                </td>
              </tr>
            ) : (
              shown.map((crew) => (
                <tr key={crew.id}>
                  <td>
                    <ToolInput
                      value={crew.name}
                      width={170}
                      onSave={(v) =>
                        void guard(() =>
                          updateRow("crews", crew.id, { name: v }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <ToolInput
                      value={crew.contact_name ?? null}
                      width={150}
                      onSave={(v) =>
                        void guard(() =>
                          updateRow("crews", crew.id, {
                            contact_name: v?.trim() || null,
                          }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <ToolInput
                      value={crew.phone}
                      width={130}
                      onSave={(v) =>
                        void guard(() =>
                          updateRow("crews", crew.id, { phone: v }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <ToolInput
                      value={crew.email}
                      width={190}
                      onSave={(v) =>
                        void guard(() =>
                          updateRow("crews", crew.id, { email: v }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      className={`tog green${crew.active ? " on" : ""}`}
                      onClick={() =>
                        void guard(() =>
                          updateRow("crews", crew.id, { active: !crew.active }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      className={`tog${crew.can_login ? " on" : ""}`}
                      onClick={() =>
                        void guard(() =>
                          updateRow("crews", crew.id, {
                            can_login: !crew.can_login,
                          }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      className={`tog${crew.is_in_house ? " on" : ""}`}
                      title={t("Our own employees — never paid out as a crew")}
                      onClick={() =>
                        void guard(() =>
                          updateRow("crews", crew.id, {
                            is_in_house: !crew.is_in_house,
                          }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <select
                      aria-label={t("{name} nail gun", { name: crew.name })}
                      value={asFramingNailer(crew.framing_nailer) ?? ""}
                      onChange={(e) =>
                        void guard(() =>
                          updateRow("crews", crew.id, {
                            framing_nailer: asFramingNailer(e.target.value),
                          }),
                        )
                      }
                    >
                      <option value="">—</option>
                      {FRAMING_NAILERS.map((gun) => (
                        <option key={gun} value={gun}>
                          {t(framingNailerLabel(gun))}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="sub" title={crew.trades.join(", ")}>
                    {collapse(crew.trades, (n) => t("{n} Trades", { n }))}
                  </td>
                  <td className="sub">
                    <InsuranceCell crew={crew} />
                  </td>
                  <td>
                    <button
                      className="btn-small"
                      onClick={() => setPapersId(crew.id)}
                      title={t(
                        "Certificates and W-9s, and the fields they fill in",
                      )}
                    >
                      {t("Documents")}
                    </button>{" "}
                    <button
                      className="btn-small"
                      onClick={() => setPacketId(crew.id)}
                      title={t(
                        "Email them the W-9, bank, insurance and agreement to fill in and sign",
                      )}
                    >
                      {t("Send packet")}
                    </button>
                  </td>
                  <td className="num">{crew.rateCount || "—"}</td>
                  <td>
                    <button
                      className="xbtn"
                      onClick={() => {
                        if (!window.confirm(t("Delete this crew?"))) return;
                        void guard(() => deleteRow("crews", crew.id));
                      }}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > shown.length && (
        <button
          className="addrow ghost"
          onClick={() => setLimit((l) => l + 60)}
        >
          {t("Show more")}
        </button>
      )}

      {creating && (
        <NewCrewModal
          // Every crew in the table, not the ones the search is showing: the
          // name that collides is as likely to be switched off and sixty rows
          // down as it is to be on screen.
          existingNames={all.map((crew) => crew.name)}
          trades={tradeList}
          onClose={() => setCreating(false)}
          onCreated={query.reload}
        />
      )}

      {packetFor && (
        <SendCrewPacketModal
          crew={packetFor}
          onClose={() => setPacketId(null)}
        />
      )}

      {papers && (
        <CrewDocumentsModal
          crew={papers}
          onClose={() => setPapersId(null)}
          onSaved={query.reload}
        />
      )}
    </>
  );
}

/**
 * A crew's insurance at a glance.
 *
 * Workers' comp first: a crew without it is the company's exposure, and an
 * expired policy is the same as none. The judgement is `crewCoverage` in
 * packages/core (rule 5) so the badge here, the banner on the documents screen
 * and anything that comes later cannot disagree about whether a crew is
 * covered — including the thirty-day warning Jonathon asked for on 2026-08-27,
 * which is the difference between finding out now and finding out on the
 * morning of the job.
 */
function InsuranceCell({ crew }: { crew: CrewRow }) {
  const coverage = crewCoverage(crew, new Date().toISOString().slice(0, 10));
  const tone =
    coverage.state === "expired" || coverage.state === "uncovered"
      ? "expired"
      : coverage.state === "expiring"
        ? "soon"
        : coverage.state === "covered"
          ? "ok"
          : "no";

  return (
    <span
      className="inswrap"
      title={[
        crew.has_wc
          ? t("WC to {date}", { date: crew.wc_expires_on ?? "—" })
          : t("no WC"),
        crew.has_gl
          ? t("GL to {date}", { date: crew.gl_expires_on ?? "—" })
          : t("no GL"),
        crew.insurer,
        crew.insurance_notes,
      ]
        .filter(Boolean)
        .join(" — ")}
    >
      <span className={`ins ${tone}`}>{coverageText(coverage)}</span>
    </span>
  );
}
